import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, theme } from 'antd';
import { SettingOutlined, ShopOutlined, ApiOutlined, MonitorOutlined, UserOutlined, SecurityScanOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';

import SellerAccountSettings from './components/SellerAccountSettings';
import WorkerManagement from './components/WorkerManagement';
import QueueMonitoring from './components/QueueMonitoring';
import TrelloSettings from './components/TrelloSettings';
import UserManagement from './components/UserManagement';
import RolesAndPermissions from './components/RolesAndPermissions';
import { fetchSellerAccounts, fetchWorkers, fetchUsers, fetchRoles } from './actions';
import './AdminSettings.css';

const { TabPane } = Tabs;

const AdminSettings = () => {
    const dispatch = useDispatch();
    const { sellerAccounts, workers, users, roles, loading } = useSelector(state => state.adminSettings);
    const [activeKey, setActiveKey] = useState('seller');
    const [loadedTabs, setLoadedTabs] = useState(new Set(['seller'])); // Track which tabs have been loaded
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    // Load initial tab data on mount
    useEffect(() => {
        // Load seller accounts initially
        dispatch(fetchSellerAccounts());
    }, [dispatch]);

    // Memoize the data fetching functions
    const fetchTabData = useCallback(async (tabKey) => {
        if (loadedTabs.has(tabKey)) {
            return; // Tab already loaded, no need to fetch again
        }

        try {
            switch (tabKey) {
                case 'seller':
                    await dispatch(fetchSellerAccounts());
                    break;
                case 'workers':
                    await dispatch(fetchWorkers());
                    break;
                case 'users':
                    await dispatch(fetchUsers());
                    await dispatch(fetchRoles()); // Also fetch roles when loading users tab
                    break;
                case 'roles':
                    await dispatch(fetchRoles()); // Fetch roles for roles tab
                    break;
                case 'queue':
                    // QueueMonitoring component handles its own data fetching
                    // No need to fetch here, just mark as loaded
                    break;
                case 'trello':
                    // TrelloSettings component handles its own data fetching
                    // No need to fetch here, just mark as loaded
                    break;
                default:
                    break;
            }
            
            // Mark tab as loaded
            setLoadedTabs(prev => new Set([...prev, tabKey]));
        } catch (error) {
            console.error(`Error loading data for tab ${tabKey}:`, error);
        }
    }, [dispatch, loadedTabs]);

    const handleTabChange = useCallback((key) => {
        setActiveKey(key);
        // Fetch data for the new tab if not already loaded
        fetchTabData(key);
    }, [fetchTabData]);

    return (
        <div
            style={{
                margin: '24px 16px',
                borderRadius: borderRadiusLG,
                minHeight: 280,
            }}
        >
            <Tabs
                activeKey={activeKey}
                onChange={handleTabChange}
                type="card"
                tabBarGutter={5}
                className="admin-settings-tabs"
            >
                <TabPane
                    tab={<span><ShopOutlined /> Seller Account</span>}
                    key="seller"
                >
                    <SellerAccountSettings
                        sellerAccounts={sellerAccounts}
                        loading={loading}
                    />
                </TabPane>
                <TabPane
                    tab={<span><ApiOutlined /> Worker Management</span>}
                    key="workers"
                >
                    <WorkerManagement
                        workers={workers}
                        loading={loading}
                    />
                </TabPane>
                <TabPane
                    tab={<span><UserOutlined /> User Management</span>}
                    key="users"
                >
                    <UserManagement
                        users={users}
                        roles={roles}
                        loading={loading}
                    />
                </TabPane>
                <TabPane
                    tab={<span><SecurityScanOutlined /> Roles & Permissions</span>}
                    key="roles"
                >
                    <RolesAndPermissions
                        roles={roles}
                        loading={loading}
                    />
                </TabPane>
                <TabPane
                    tab={<span><MonitorOutlined /> Queue Monitoring</span>}
                    key="queue"
                >
                    <QueueMonitoring />
                </TabPane>
                <TabPane
                    tab={<span><ApiOutlined /> Trello Settings</span>}
                    key="trello"
                >
                    <TrelloSettings />
                </TabPane>
            </Tabs>
        </div>
    );
};

export default AdminSettings;